'use client';

import * as React from 'react';
import { clsx } from 'clsx';
import styles from './Tag.module.css';

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * Visual variant
   * - default: Subtle outlined style
   * - skill: Black border skill tags with icon/logo
   * - minimal: Light gray background, subtle appearance
   * - minimalOutline: Light gray border, no fill
   * - filled: Filled background
   * - signal: For status indicators (uses signal colors)
   */
  variant?: 'default' | 'skill' | 'minimal' | 'minimalOutline' | 'filled' | 'signal';

  /**
   * Color scheme
   * - neutral: Gray tones
   * - primary: Indigo/product color
   * - success: Green
   * - warning: Orange
   * - danger: Red
   * - info: Blue
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info';

  /**
   * Size variant
   * - sm: Compact
   * - md: Default
   */
  size?: 'sm' | 'md';

  /**
   * Make the tag removable (shows X button)
   */
  removable?: boolean;

  /**
   * Callback when remove button is clicked
   */
  onRemove?: () => void;

  /**
   * Icon to display before the label
   */
  icon?: React.ReactNode;

  /**
   * Selection state for interactive skill tags. When explicitly false, the
   * icon and label use the muted unselected treatment.
   */
  selected?: boolean;

  /**
   * Optional count badge shown after the label (e.g. "Candidates added 3")
   */
  count?: number | string;

  /**
   * Additional class names
   */
  className?: string;
}

/**
 * Tag Component (Skill Tags, Badges)
 *
 * Laws:
 * - Radius: always pill (999px)
 * - Tags have personality - the rest of the UI cannot
 * - Delicate: thin borders, transparent or very light backgrounds
 */
export const Tag = React.forwardRef<HTMLSpanElement, TagProps>(
  (
    {
      variant = 'default',
      color = 'primary',
      size = 'md',
      removable = false,
      onRemove,
      icon,
      selected,
      count,
      className,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <span
        ref={ref}
        className={clsx(
          styles.tag,
          styles[variant],
          styles[color],
          styles[size],
          variant === 'skill' && selected === false && styles.skillUnselected,
          className
        )}
        {...props}
      >
        {icon && <span className={styles.icon}>{icon}</span>}
        <span className={styles.label}>{children}</span>
        {count !== undefined && <span className={styles.counter}>{count}</span>}
        {removable && (
          <button
            type="button"
            className={styles.removeButton}
            onClick={(e) => {
              e.stopPropagation();
              onRemove?.();
            }}
            aria-label="Remove"
          >
            <svg viewBox="0 0 16 16" fill="currentColor" className={styles.removeIcon}>
              <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
            </svg>
          </button>
        )}
      </span>
    );
  }
);

Tag.displayName = 'Tag';

/* ===========================================
   METRIC BADGE (For proof chips like "109%")
   =========================================== */

export interface MetricBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  /**
   * The metric value (e.g., "+109%", "42")
   */
  value: string | number;

  /**
   * Whether the metric is positive (green), negative (red), or neutral
   */
  trend?: 'up' | 'down' | 'neutral';

  /**
   * Size
   */
  size?: 'sm' | 'md' | 'lg';

  className?: string;
}

export const MetricBadge = React.forwardRef<HTMLSpanElement, MetricBadgeProps>(
  ({ value, trend = 'neutral', size = 'md', className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={clsx(styles.metricBadge, styles[`metric-${trend}`], styles[`metric-${size}`], className)}
        {...props}
      >
        {trend === 'up' && (
          <svg viewBox="0 0 16 16" fill="currentColor" className={styles.trendIcon}>
            <path d="M8 4l4 4H9v4H7V8H4l4-4z" />
          </svg>
        )}
        {trend === 'down' && (
          <svg viewBox="0 0 16 16" fill="currentColor" className={styles.trendIcon}>
            <path d="M8 12l-4-4h3V4h2v4h3l-4 4z" />
          </svg>
        )}
        {value}
      </span>
    );
  }
);

MetricBadge.displayName = 'MetricBadge';

export default Tag;
